/* Compact product offers. This is a storefront guide, NOT checkout validation. */
(() => {
  'use strict';
  const selector = '[data-cosmoshop-item-offers]';
  const formatNZD = (cents) => 'NZ$' + (Math.max(0, cents) / 100).toLocaleString('en-NZ', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
  let pendingRequest = null;
  let lastCart = null;

  function applyCart(widget, cart) {
    const subtotal = cart && Number.isFinite(Number(cart.items_subtotal_price)) ? Number(cart.items_subtotal_price) : NaN;
    const fallback = Number(widget.dataset.cartSubtotal || '0');
    const totalCents = Number.isFinite(subtotal) ? subtotal : fallback;
    const cartItems = Array.isArray(cart && cart.items) ? cart.items : [];

    widget.querySelectorAll('[data-offer-type]').forEach((row) => {
      const status = row.querySelector('[data-offer-status]');
      if (!status) return;
      let ready = false;
      if (row.dataset.offerType === 'brand') {
        const brand = (row.dataset.offerBrand || '').trim().toLowerCase();
        ready = cart && Array.isArray(cart.items)
          ? cartItems.some((item) => (item.vendor || '').trim().toLowerCase() === brand)
          : widget.dataset.brandInCart === 'true';
        const displayBrand = brand === 'dkny' ? 'DKNY' : brand.charAt(0).toUpperCase() + brand.slice(1);
        status.textContent = ready
          ? `${displayBrand} item in bag · Subject to checkout eligibility`
          : `For ${displayBrand} items · Add to bag to use`;
      } else if (row.dataset.offerType === 'free-product') {
        const thresholdCents = Number(row.dataset.offerMinCents || 0);
        const auraInCart = cart && Array.isArray(cart.items)
          ? cartItems.some((item) => (item.vendor || '').trim().toLowerCase() === 'aura jewellery')
          : row.dataset.offerAuraInCart === 'true';
        const spendMet = totalCents > thresholdCents;
        ready = spendMet && auraInCart;
        status.textContent = !spendMet
          ? `Spend ${formatNZD(thresholdCents + 1 - totalCents)} more to unlock`
          : auraInCart
            ? 'Order spend met · Subject to checkout eligibility'
            : 'Order spend met · Add any Aura jewellery to bag';
      } else {
        const thresholdCents = Number(row.dataset.offerMinCents || 0);
        // "Over" is strict: an order at exactly NZ$200.00 is not over NZ$200.
        ready = totalCents > thresholdCents;
        const delta = Math.max(0, thresholdCents + 1 - totalCents);
        status.textContent = ready
          ? 'Order spend met · Subject to checkout eligibility'
          : `Spend ${formatNZD(delta)} more to unlock`;
      }
      row.classList.toggle('is-ready', ready);
      row.querySelectorAll('[data-offer-copy]').forEach((button) => {
        button.setAttribute('aria-description', ready ? 'Copy for checkout' : 'Copy for later; conditions not yet met');
      });
    });
  }

  function updateAll(cart) {
    document.querySelectorAll(selector).forEach((widget) => applyCart(widget, cart));
  }

  async function refreshCart() {
    if (pendingRequest) return pendingRequest;
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    pendingRequest = fetch(`${root.replace(/\/$/, '')}/cart.js`, {
      credentials: 'same-origin', headers: { 'Accept': 'application/json' }, cache: 'no-store'
    }).then((res) => {
      if (!res.ok) throw new Error('Cart unavailable');
      return res.json();
    }).then((cart) => {
      lastCart = cart;
      updateAll(cart);
      return cart;
    }).catch(() => {
      updateAll(null);
    }).finally(() => { pendingRequest = null; });
    return pendingRequest;
  }

  function setCopiedState(button, copied) {
    if (!button) return;
    if (button._copiedTimeout) {
      window.clearTimeout(button._copiedTimeout);
      button._copiedTimeout = null;
    }
    button.classList.toggle('is-copied', copied);
    if (copied) {
      button.setAttribute('aria-label', `Copied discount code ${button.getAttribute('data-offer-copy')}`);
      button._copiedTimeout = window.setTimeout(() => {
        button.classList.remove('is-copied');
        button.setAttribute('aria-label', `Copy discount code ${button.getAttribute('data-offer-copy')}`);
        button._copiedTimeout = null;
      }, 1800);
    } else {
      button.setAttribute('aria-label', `Copy discount code ${button.getAttribute('data-offer-copy')}`);
    }
  }

  async function copyCode(button) {
    const code = button.getAttribute('data-offer-copy');
    if (!code) return;
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        copied = true;
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.style.cssText = 'position:fixed;left:-9999px;top:0';
        document.body.appendChild(textarea);
        textarea.select();
        copied = document.execCommand('copy');
        textarea.remove();
      }
    } catch (error) { copied = false; }
    const widget = button.closest(selector);
    const feedback = widget && widget.querySelector('[data-offer-feedback]');
    if (feedback) feedback.textContent = copied ? `${code} copied` : `Select and copy ${code}`;
    setCopiedState(button, copied);
    if (!copied) window.prompt('Copy discount code:', code);
  }

  // If an older, separately injected offers card is present, hide only that
  // specifically identifiable card to avoid showing the old discount codes.
  function hideLegacyOfferCards() {
    document.querySelectorAll('.cosmoshop-product-page *').forEach((node) => {
      if (node.children.length > 2 || node.closest(selector)) return;
      if ((node.textContent || '').trim() !== 'Offers on this item') return;
      let parent = node.parentElement;
      for (let i = 0; i < 6 && parent; i++, parent = parent.parentElement) {
        if (parent.matches(selector)) break;
        const content = parent.textContent || '';
        if (content.includes('Apply at checkout') &&
            /\b(CITIZEN20|BULOVA20|NY20|CARD15|FIRST10)\b/.test(content) &&
            content.length < 4500) {
          parent.hidden = true;
          parent.style.display = 'none';
          parent.setAttribute('aria-hidden', 'true');
          break;
        }
      }
    });
  }

  function initialize() {
    if (!document.querySelector(selector)) return;
    updateAll(lastCart);
    refreshCart();
    hideLegacyOfferCards();
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cosmoshop-item-offers] [data-offer-copy]');
    if (button) {
      event.preventDefault();
      copyCode(button);
      return;
    }
    const toggle = event.target.closest('[data-cosmoshop-item-offers] [data-offer-toggle]');
    if (toggle) {
      const widget = toggle.closest(selector);
      const extra = widget && widget.querySelector('[data-offer-extra]');
      if (extra) {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        extra.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        widget.classList.toggle('is-open', open);
      }
      return;
    }
    if (event.target.closest('.cosmoshop-product-page [name="add"][type="submit"]')) {
      window.setTimeout(refreshCart, 800);
      window.setTimeout(refreshCart, 1850);
    }
  });
  document.addEventListener('cart:updated', refreshCart);
  document.addEventListener('cart:refresh', refreshCart);
  document.addEventListener('cart:change', refreshCart);
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refreshCart();
  });
  window.addEventListener('pageshow', refreshCart);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  // An app embed may insert the previous version later than DOMContentLoaded.
  window.addEventListener('load', () => { hideLegacyOfferCards(); window.setTimeout(hideLegacyOfferCards, 1800); });
})();
